import { concatHex, encodeAbiParameters, keccak256, toBytes, zeroHash, type Hex } from "viem";

export const SCORE_SCHEMA_VERSION = "matic-moon-agent-scores/v1";

export type ScoreFeedback = {
  registry_address: string;
  subject_agent_id: number | string;
  subject_controller_epoch: number;
  reviewer_address: string;
  feedback_index: number;
  value: number | string;
  value_decimals: number;
  tags: unknown;
  verified_agent: boolean;
  revoked_at?: string | null;
};

export type ScoreValidation = {
  registry_address: string;
  request_hash: string;
  agent_id: number | string;
  validator_address: string;
  response: number | null;
  tag: string | null;
};

export type DimensionScore = {
  dimension: string;
  scoreBps: number;
  confidenceBps: number;
  sampleCount: number;
  evidenceWeight: number;
};

export type AgentScore = {
  agentId: string;
  controllerEpoch: number;
  dimensions: DimensionScore[];
  evidenceHash: Hex;
  leaf: Hex;
};

export type ScoreDataset = {
  schema: typeof SCORE_SCHEMA_VERSION;
  chainId: 137;
  namespace: string;
  dimensions: string[];
  dimensionsHash: Hex;
  root: Hex;
  agents: AgentScore[];
};

const normalizedDimension = (value: unknown) => String(value ?? "").trim().toLowerCase()
  .replace(/[^a-z0-9:_-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64);

const tagsOf = (tags: unknown) => {
  let values = tags;
  if (typeof tags === "string") {
    try { values = JSON.parse(tags); } catch { values = [tags]; }
  }
  const result = Array.isArray(values) ? values.map(normalizedDimension).filter(Boolean) : [];
  return [...new Set(result.length ? result : ["overall"])].sort();
};

const merkleRoot = (input: Hex[]): Hex => {
  if (!input.length) return zeroHash;
  let level = [...input].sort();
  while (level.length > 1) {
    const next: Hex[] = [];
    for (let index = 0; index < level.length; index += 2) {
      const left = level[index];
      const right = level[index + 1] ?? left;
      const pair = left.toLowerCase() <= right.toLowerCase() ? [left, right] : [right, left];
      next.push(keccak256(concatHex(pair)));
    }
    level = next.sort();
  }
  return level[0];
};

export function computeAgentScoreDataset(
  namespaceInput: string,
  feedback: ScoreFeedback[],
  validations: ScoreValidation[],
): ScoreDataset {
  const namespace = normalizedDimension(namespaceInput) || "agents";
  const observations = new Map<string, Map<string, { weightedBps: number; weight: number; count: number; evidence: string[] }>>();
  const epochs = new Map<string, number>();
  const observe = (agentId: string, dimension: string, scoreBps: number, weight: number, evidence: string) => {
    const dimensions = observations.get(agentId) ?? new Map();
    const aggregate = dimensions.get(dimension) ?? { weightedBps: 0, weight: 0, count: 0, evidence: [] };
    aggregate.weightedBps += scoreBps * weight; aggregate.weight += weight; aggregate.count += 1; aggregate.evidence.push(evidence);
    dimensions.set(dimension, aggregate); observations.set(agentId, dimensions);
  };

  feedback.filter((item) => !item.revoked_at).forEach((item) => {
    const agentId = String(item.subject_agent_id); const decimals = Math.max(0, Math.min(18, Number(item.value_decimals)));
    const value = Number(item.value) / 10 ** decimals; const scoreBps = Math.round(Math.max(0, Math.min(100, value)) * 100);
    const weight = item.verified_agent ? 2 : 1;
    epochs.set(agentId, Math.max(epochs.get(agentId) ?? 0, Number(item.subject_controller_epoch) || 0));
    tagsOf(item.tags).forEach((dimension) => observe(agentId, dimension, scoreBps, weight,
      `f:${item.registry_address.toLowerCase()}:${item.reviewer_address.toLowerCase()}:${item.feedback_index}`));
  });
  validations.filter((item) => item.response !== null).forEach((item) => {
    const agentId = String(item.agent_id); const dimension = normalizedDimension(item.tag) || "validation";
    observe(agentId, dimension, Math.round(Math.max(0, Math.min(100, Number(item.response))) * 100), 2,
      `v:${item.registry_address.toLowerCase()}:${item.request_hash.toLowerCase()}:${item.validator_address.toLowerCase()}`);
  });

  const allDimensions = [...new Set([...observations.values()].flatMap((items) => [...items.keys()]))].sort();
  const dimensionsHash = keccak256(toBytes(allDimensions.join(",")));
  const namespaceHash = keccak256(toBytes(namespace));
  const agents = [...observations.entries()].sort(([a], [b]) => BigInt(a) < BigInt(b) ? -1 : BigInt(a) > BigInt(b) ? 1 : 0)
    .map(([agentId, items]) => {
      const evidence = [...new Set([...items.values()].flatMap((item) => item.evidence))].sort();
      const evidenceHash = keccak256(toBytes(evidence.join("\n")));
      const dimensions = [...items.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([dimension, item]) => ({
        dimension, scoreBps: Math.round(item.weightedBps / item.weight),
        confidenceBps: Math.round((item.weight / (item.weight + 5)) * 10_000), sampleCount: item.count, evidenceWeight: item.weight,
      }));
      const scoresHash = keccak256(toBytes(dimensions.map((item) => `${item.dimension}:${item.scoreBps}:${item.confidenceBps}:${item.sampleCount}:${item.evidenceWeight}`).join("\n")));
      const controllerEpoch = epochs.get(agentId) ?? 0;
      const leaf = keccak256(encodeAbiParameters(
        [{ type: "uint256" }, { type: "bytes32" }, { type: "uint256" }, { type: "uint64" }, { type: "bytes32" }, { type: "bytes32" }],
        [137n, namespaceHash, BigInt(agentId), BigInt(controllerEpoch), scoresHash, evidenceHash],
      ));
      return { agentId, controllerEpoch, dimensions, evidenceHash, leaf };
    });
  return { schema: SCORE_SCHEMA_VERSION, chainId: 137, namespace, dimensions: allDimensions, dimensionsHash, root: merkleRoot(agents.map((item) => item.leaf)), agents };
}
